import React, { useState, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import LocalStorageService from '../../services/LocalStorageService'
import NotificationService from '../../services/NotificationService'
import OrganisationService from '../../services/OrganisationService'
import UserService from '../../services/UserService'
import RoleService from '../../services/RoleService'

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Appt Components
import Anchor from '../../components/simple/anchor/Anchor'
import Box from '../../components/simple/box/Box'
import Divider from '../../components/simple/divider/Divider'
import Form from '../../components/simple/form/Form.js'
import FormField from '../../components/simple/formField/FormField.js'
import Select from '../../components/simple/input/Select'
import Text from '../../components/simple/text/Text'
import TextInput from '../../components/simple/input/TextInput'

// Shared Components
import QuickActionBar from '../shared/QuickActionBar'
import BreadcrumbBar from '../shared/BreadcrumbBar'
import ActionButton from '../../components/compound/actionButton/ActionButton.js'
import CheckBox from '../../components/simple/input/CheckBox.js'
import OrganisationTagService from '../../services/OrganisationTagService.js'
import PasswordService from '../../services/PasswordService.js'

function UserEdit (props) {
  const navigate = useNavigate()

  const apiToken = LocalStorageService.get('apiToken')
  const activeOrg = LocalStorageService.get('activeOrg')

  // Initialize userDetails otherwise we get error
  // A component is changing an uncontrolled input of type text to be controlled
  const [userDetails, setUserDetails] = useState({
    organisation: 0,
    role: 0,
    // username: '',
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: ''
  })

  // Contains array of org objects for populating the School dropdown
  const [orgValues, setOrgValues] = useState([])

  const [saveLocked, setSaveLocked] = useState(false)


  // Contains an Organisation object that has been selected from the School dropdown
  const [selectedOrg, setSelectedOrg] = useState(null)

  // Contains array of role objects for populating the Role dropdown
  const [roleValues, setRoleValues] = useState([])

  const [originalRole, setOriginalRole] = useState(null)

  const [safeguardingLeads, setSafeguardingLeads] = useState([])

  // const roles = ['Teacher', 'Supervisor', 'Headteacher']

  const params = useParams()

  const userId = params.userid

  // Submit form values
  const submitForm = async (values) => {
    if (userId === 'new') {

      setSaveLocked(true)

      var userAdded = await UserService.inviteUser(apiToken, values)
      if (userAdded?.error) {
        // If the error message is about a user limit, display it
        var errorStr = 'Error saving the user, please try again.'
        if (userAdded?.error?.errorType === 'userlimit') {
          errorStr = userAdded.error.error
        }
        NotificationService.error(errorStr)
      } else {
        NotificationService.info('User added')
        navigate('/users', { message: userAdded.data.message })
      }
    } else {
      var userUpdated = await UserService.updateUser(apiToken, values)
      if (userUpdated?.error) {
        NotificationService.error('Error saving the user, please try again - ', userUpdated?.error)
        return
      }

      // if the role has changed, update the role
      if (values.role && originalRole !== values.role) {
        var roleUpdated = await RoleService.addRoleUser(apiToken, null, values.role, values.id)
        if (roleUpdated?.error) {
          NotificationService.error('Error saving the user, please try again - ', roleUpdated?.error)
          return
        }
      }

      NotificationService.success('User updated successfully')
      navigate('/users', { message: 'User updated successfully' })

    }
  }

  // A different school has been selected
  const changeOrganisation = (org) => {
    setSelectedOrg(org)
  }

  // Reset User password
  const resetPassword = async (email) => {
    try {
      var passwordResetStatus = await PasswordService.resetPasswordRequest(null, email)
    } catch (err) {
      NotificationService.error('Password reset error - ', err)
      return
    }

    NotificationService.success('Password reset email has been sent', passwordResetStatus)
  }

  const deleteUser = () => {
    navigate('userdelete')
  }

  // Go Back
  const goBack = () => {
    navigate(-1)
  }

  useEffect(() => {
    // get the current user's role
    const getRoleForUser = async () => {
      // don't do anything if the user is new
      if (userId === 'new') {
        return
      }

      // don't do anything if we don't have any roles
      if (!roleValues.length) {
        return
      }

      // don't do anything if we don't have a selectedOrg
      if (!selectedOrg.id) {
        return
      }

      const roles = await RoleService.getRoleForUser(apiToken, userId)
      if (roles?.error) {
        NotificationService.error(roles.error)
      } else if (roles?.data) {
        // get the role that belongs to the current organisation
        var roleForThisOrg = roles.data.find(role => parseInt(role.organisation) === selectedOrg.id)
        if (roleForThisOrg) {
          setUserDetails({
            ...userDetails,
            role: roleForThisOrg.id
          })

          setOriginalRole(roleForThisOrg.id)

        }
      }
    }

    if (selectedOrg) {
      getRoleForUser()
    }
  }, [roleValues, selectedOrg])

  useEffect(() => {
    const getRoles = async () => {
      // don't do anything if we don't have a selected org
      if (!selectedOrg?.id) {
        return
      }
      const roles = await OrganisationService.getRolesForOrg(apiToken, selectedOrg.id)
      if (roles?.error) {
        NotificationService.error(roles.error)
      } else if (roles?.data) {

        for (let role of roles.data) {
          if ( role.name === 'Admin') {
            role.name = 'Full User'
          } else if (role.name === 'Standard') {
            role.name = 'Restricted User'
          }
        }
        setRoleValues(roles.data)
      }
    }

    if (selectedOrg) {
      getRoles()
    }
  }, [selectedOrg])

  useEffect(() => {
    let unmounted = false
    const getUserDetails = async (userId) => {
      const params = {
        fields: 'id,firstName,lastName,email,jobTitle',
        type: 'user'
      }

      const response = await UserService.getUser(apiToken, params, userId)
      if (response?.error) {
        NotificationService.error(response.error)
      } else {
        var newDetails = {
          ...userDetails,
          ...response.data[0]
        }
        setUserDetails(newDetails)
      }
    }
    if (!unmounted) {
      if (userId !== 'new') {
        getUserDetails(userId)
      }
    }
    return () => { unmounted = true }
  }, [orgValues])

  useEffect(() => {
    let unmounted = false

    const getOrganisations = async () => {
      var organisations = LocalStorageService.get('userOrganisations')
      if (organisations?.error) {
        NotificationService.error(organisations.error)
      } else {
        setOrgValues(organisations)
        setSelectedOrg(activeOrg)

        // make sure the organisations comes back as an array
        var toSave = {
          ...userDetails,
          organisation: activeOrg.id
        }
        setUserDetails(toSave)
      }
    }
    if (!unmounted) {
      getOrganisations()
    }
    return () => { unmounted = true }
  }, [])

  const getSafeguardingLeads = async () => {

    const params = {
      fields: 'type,key,value',
      limit: 100,
      orgId: activeOrg.id
    }

    const where = {
      type: 'besmart'
    }

    const organisationTags = await OrganisationTagService.get(apiToken, params, where)
    if (organisationTags) {
      organisationTags.forEach(tag => {
        if (tag.key === 'safeguardingLeads') {
          try {
              let sLeads = JSON.parse(tag.value)
              setSafeguardingLeads(sLeads)
            } catch (err) {
              console.error(err)
            }
        }
      })
    }

  }

  useEffect(() => {
    getSafeguardingLeads()
  }, [])


  return (
    <Box width='xlarge'>
      <BreadcrumbBar
        title={`User: ${userDetails.id ? userDetails.firstName + ' ' + userDetails.lastName : 'New User'}`}
        path={<><Anchor href='/'>BehaviourSmart</Anchor><Text color='besmartBlue' size='xsmall'>{window.location.pathname}</Text></>}
      />

      {userDetails.id &&
        <QuickActionBar button1Click={() => resetPassword(userDetails.email)} button1Label={<Text><FontAwesomeIcon icon={['fal', 'key']} /> Reset Password</Text>} button2Click={deleteUser} button2Label={<Text><FontAwesomeIcon icon={['fal', 'trash']} /> Delete User</Text>} />}

      <Box background='white' direction='column' gap='small' round='small' flex='grow'>
        <Box
          gap='small'
          margin={{ horizontal: 'small' }}
          pad='small'
          round='small'
        >

          {userId === 'new' &&
            <>
              <Text margin={{ top: 'medium' }} size='xlarge'>Invite User</Text>
              <Text margin={{ top: 'xsmall' }} size='small'>Enter the email address of the user you want to invite to your service in BehaviourSmart. They'll be sent a link via email inviting them to create an account.</Text>
            </>}

          {userId !== 'new' && <Text size='xlarge'>User Details</Text> }


          <Box direction='column' gap='medium'>

            <Form
              //   validate='blur'
              onChange={nextValue => {
                setUserDetails(nextValue)
              }}
              onSubmit={({ value: nextValue }) => {
                submitForm(nextValue)
              }}
              value={userDetails}
            >
              <Box direction='row-responsive' gap='medium' margin={{ bottom: 'medium', top: 'none'}}>

                <FormField
                  label='System Role'
                  name='role'
                  required
                >
                  <Select
                    id='role'
                    name='role'
                    labelKey='title'
                    placeholder='System Role'
                    options={roleValues}
                    valueKey={{ key: 'id', reduce: true }}
                  />
                </FormField>

                <FormField
                  label='Enhanced Permissions'
                  name='permissions'
                >
                  <CheckBox
                    name='permissions'
                    label='Access to the Analytics, System Users, and all Incidents'
                    checked={safeguardingLeads.map(lead => lead.email).includes(userDetails.email)}
                    onChange={(event) => {

                      // console.log("event", event.value)

                      setSafeguardingLeads(prevLeads => {
                        if (event?.target?.checked) {
                          return [...prevLeads, { email: userDetails.email }]
                        } else {
                          return prevLeads.filter(lead => lead.email !== userDetails.email)
                        }
                      })
                    }}
                  />

                </FormField>

                {userId === 'new' &&
                  <FormField
                    label='Email'
                    name='email'
                    required
                  >
                    <TextInput
                      name='email'
                      type='email'
                    />
                  </FormField>}


              </Box>

              {userId !== 'new' &&
                <Box>

                  <Box direction='row-responsive' gap='medium'>
                    <FormField
                      label='Firstname'
                      name='firstName'
                      required
                    >
                      <TextInput
                        name='firstName'
                        type='text'
                      />
                    </FormField>

                    <FormField
                      label='Lastname'
                      name='lastName'
                      required
                    >
                      <TextInput
                        name='lastName'
                        type='text'
                      />
                    </FormField>
                  </Box>

                  <Box direction='row-responsive' gap='medium'>
                    <FormField
                      label='Email'
                      name='email'
                      required
                    >
                      <TextInput
                        name='email'
                        type='email'
                      />
                    </FormField>

                    <FormField
                      label='Job Title'
                      name='jobTitle'
                      required
                    >
                      <TextInput
                        name='jobTitle'
                        type='text'
                      />
                    </FormField>
                  </Box>
                </Box>}




              <Divider color='primary' margin={{ top: 'medium', bottom: 'none' }} />

              <Box direction='row' justify='between' margin={{ top: 'medium' }}>
                <ActionButton label='< Back' onClick={() => goBack()} secondary />
                <ActionButton type='submit' label={(userId === 'new' ? 'Send Invite' : 'Save')} primary disabled={saveLocked} />
              </Box>
            </Form>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default UserEdit
